import React, { useState, useEffect, useCallback } from 'react';

const FORMATS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'DOCX' },
  { value: 'xlsx', label: 'XLSX' },
  { value: 'md', label: 'Markdown' },
];

export default function Generate() {
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [instruction, setInstruction] = useState('');
  const [outputFormat, setOutputFormat] = useState('pdf');
  const [mode, setMode] = useState('template');
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetch('/api/generate/templates').then(r => r.ok ? r.json() : []).then(d => setTemplates(Array.isArray(d) ? d : [])).catch(() => {});
    fetch('/api/documents?page=1&page_size=100&status=ready').then(r => r.ok ? r.json() : {}).then(d => setDocuments(Array.isArray(d?.documents) ? d.documents : [])).catch(() => {});
  }, []);

  const toggleDoc = (id) => setSelectedDocs(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });

  const startGeneration = useCallback(async () => {
    if (!selectedDocs.size) return;
    setGenerating(true); setError(null); setJob(null);
    try {
      const body = { source_document_ids: [...selectedDocs], template: mode === 'template' ? selectedTemplate : null, instruction: mode === 'custom' ? instruction : null, output_format: outputFormat };
      const r = await fetch('/api/generate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      const poll = async () => {
        const pr = await fetch(`/api/generate/${d.job_id}`);
        const pj = await pr.json();
        setJob(pj);
        if (pj.status === 'processing') setTimeout(poll, 2000);
        else setGenerating(false);
      };
      poll();
    } catch (e) { setError(e.message); setGenerating(false); }
  }, [selectedDocs, mode, selectedTemplate, instruction, outputFormat]);

  const loadHistory = async () => {
    try { const r = await fetch('/api/generate/history?page=1&page_size=20'); if (r.ok) { const d = await r.json(); setHistory(Array.isArray(d) ? d : d.jobs || []); } } catch {}
    setShowHistory(true);
  };

  return (
    <div style={{ padding: 'var(--gap-lg)', maxWidth: 900, overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--gap-lg)' }}>
        <h2 style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>Generator</h2>
        <button onClick={loadHistory} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '6px 12px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>Historie</button>
      </div>

      {showHistory ? (
        <div>
          <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', color: '#bb9af7', cursor: 'pointer', marginBottom: 12, fontSize: 'var(--font-size-sm)' }}>{'\u2190'} Zurueck</button>
          {(Array.isArray(history) ? history : history.jobs || []).map(h => (
            <div key={h.job_id} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: 12, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><span style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)' }}>{h.template || h.instruction || '-'}</span> <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>{h.output_format}</span></div>
              {h.status === 'ready' && <button onClick={() => window.open(`/api/generate/${h.job_id}/download`)} style={{ background: '#bb9af7', border: 'none', borderRadius: 4, padding: '4px 10px', color: '#1a1b26', cursor: 'pointer', fontSize: 12 }}>Download</button>}
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Documents */}
          <div style={{ marginBottom: 'var(--gap-md)' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Quelldokumente</div>
            <div style={{ maxHeight: 200, overflow: 'auto', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: 8 }}>
              {documents.map(d => (
                <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 4, color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)' }}>
                  <input type="checkbox" checked={selectedDocs.has(d.id)} onChange={() => toggleDoc(d.id)} />
                  {d.title || d.filename}
                </label>
              ))}
              {documents.length === 0 && <div style={{ color: 'var(--text-muted)', padding: 8 }}>Keine Dokumente bereit</div>}
            </div>
          </div>

          {/* Mode */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--gap-md)' }}>
            {['template', 'custom'].map(m => (
              <button key={m} onClick={() => setMode(m)} style={{ background: mode === m ? 'rgba(187,154,247,0.2)' : 'var(--bg-tertiary)', border: `1px solid ${mode === m ? '#bb9af7' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: '6px 14px', color: mode === m ? '#bb9af7' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                {m === 'template' ? 'Vorlage' : 'Freitext'}
              </button>
            ))}
          </div>

          {mode === 'template' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 'var(--gap-md)' }}>
              {templates.map(t => (
                <button key={t.id} onClick={() => setSelectedTemplate(t.id)} style={{ background: selectedTemplate === t.id ? 'rgba(187,154,247,0.15)' : 'var(--bg-secondary)', border: `1px solid ${selectedTemplate === t.id ? '#bb9af7' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: 12, cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ color: selectedTemplate === t.id ? '#bb9af7' : 'var(--text-primary)', fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{t.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginTop: 4 }}>{t.description}</div>
                </button>
              ))}
            </div>
          ) : (
            <textarea value={instruction} onChange={e => setInstruction(e.target.value)} placeholder="Was soll generiert werden?" rows={4} style={{ width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', resize: 'vertical', outline: 'none', marginBottom: 'var(--gap-md)' }} />
          )}

          {/* Format */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--gap-md)' }}>
            {FORMATS.map(f => (
              <button key={f.value} onClick={() => setOutputFormat(f.value)} style={{ background: outputFormat === f.value ? 'rgba(187,154,247,0.2)' : 'var(--bg-tertiary)', border: `1px solid ${outputFormat === f.value ? '#bb9af7' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: '8px 16px', color: outputFormat === f.value ? '#bb9af7' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                {f.label}
              </button>
            ))}
          </div>

          <button onClick={startGeneration} disabled={!selectedDocs.size || generating} style={{ background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '10px 24px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer', opacity: (!selectedDocs.size || generating) ? 0.5 : 1 }}>
            {generating ? 'Generiere...' : 'Generieren'}
          </button>

          {error && <div style={{ color: '#f7768e', marginTop: 12, fontSize: 'var(--font-size-sm)' }}>{error}</div>}

          {job && (
            <div style={{ marginTop: 'var(--gap-lg)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius-lg)', padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ color: job.status === 'ready' ? '#9ece6a' : job.status === 'error' ? '#f7768e' : '#7aa2f7', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{job.status === 'ready' ? 'Fertig' : job.status === 'error' ? 'Fehler' : 'Verarbeite...'}</span>
                {job.processing_time_ms && <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>{(job.processing_time_ms / 1000).toFixed(1)}s</span>}
              </div>
              {job.status === 'ready' && <button onClick={() => window.open(`/api/generate/${job.job_id}/download`)} style={{ background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '8px 16px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer' }}>Download</button>}
              {job.error_message && <div style={{ color: '#f7768e', marginTop: 8, fontSize: 'var(--font-size-sm)' }}>{job.error_message}</div>}
              {job.preview_html && <div style={{ marginTop: 12, background: 'var(--bg-primary)', borderRadius: 'var(--border-radius)', padding: 16 }} dangerouslySetInnerHTML={{ __html: job.preview_html }} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
